import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useWindows } from '../../contexts/WindowContext';
import { getGradientClasses } from '../../utils/helpers';
import { SearchIcon, LightbulbIcon, PencilIcon, CodeIcon, ZapIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
export function ProcessApp() {
  const {
    getThemeClasses,
    theme
  } = useTheme();
  const {
    isMobile
  } = useWindows();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const processSteps = [{
    id: 'discover',
    title: 'Ontdekken',
    subtitle: 'Inzicht & Context',
    description: 'Begrijpen van de gebruiker, het doel en de context. Elk idee start met observatie en analyse.',
    icon: SearchIcon,
    color: 'from-blue-500 to-purple-500',
    rotation: -3,
    activities: ['Gebruikersobservatie en onderzoek', 'Contextanalyse', 'Doeldefinitie', 'Stakeholder afstemming'],
    benefits: ['Helder begrip van echte behoeften', 'Afgestemde verwachtingen vanaf het begin', 'Voorkomen van onnodige complexiteit']
  }, {
    id: 'define',
    title: 'Definiëren',
    subtitle: 'Concept & Richting',
    description: 'Vertalen van inzichten naar een visueel en functioneel concept. Storytelling en UX gaan hier hand in hand.',
    icon: LightbulbIcon,
    color: 'from-purple-500 to-pink-500',
    rotation: 2,
    activities: ['Conceptuele verkenning', 'Visuele richting bepalen', 'Storytelling raamwerk', 'UX-strategie ontwikkeling'],
    benefits: ['Duidelijke creatieve richting', 'Afgestemde visie tussen stakeholders', 'Innovatieve maar haalbare concepten']
  }, {
    id: 'design',
    title: 'Ontwerpen',
    subtitle: 'Prototype & Validatie',
    description: 'Bouwen van AI- of UX-prototypes met focus op flow en gevoel.',
    icon: PencilIcon,
    color: 'from-pink-500 to-red-500',
    rotation: -1,
    activities: ['Interactief prototyping', 'Flow validatie', 'Gebruikerstesten', 'Iteratieve verfijning'],
    benefits: ['Vroege validatie van concepten', "Verminderde ontwikkelrisico's", 'Gebruikersgeteste interacties']
  }, {
    id: 'develop',
    title: 'Ontwikkelen',
    subtitle: 'Fine-tuning & Consistentie',
    description: 'Visuele afstemming, micro-interacties, en UX-detailing voor een naadloze ervaring.',
    icon: CodeIcon,
    color: 'from-red-500 to-orange-500',
    rotation: 3,
    activities: ['Visuele polish', 'Micro-interactie design', 'Consistentiecontrole', 'Toegankelijkheid verfijning'],
    benefits: ['Professioneel, gepolijst resultaat', 'Consistente gebruikerservaring', 'Aandacht voor detail']
  }, {
    id: 'deliver',
    title: 'Leveren',
    subtitle: 'AI-integratie & Realisatie',
    description: 'Toepassen van AI om gedrag, data en creativiteit te verbinden. Altijd met een menselijke kern.',
    icon: ZapIcon,
    color: 'from-orange-500 to-amber-500',
    rotation: -2,
    activities: ['AI-implementatie', 'Data-integratie', 'Gedragsoptimalisatie', 'Mensgerichte verfijning'],
    benefits: ['Intelligente, adaptieve oplossingen', 'Data-gedreven inzichten', 'Schaalbare technologie']
  }];
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} h-full overflow-y-auto custom-scrollbar`} data-id="element-331">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6 md:mb-8" data-id="element-332">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3" data-id="element-333">
          Mijn Werkwijze
        </h1>
        <p className={`text-base md:text-lg ${themeClasses.text} opacity-70 max-w-3xl`} data-id="element-334">
          Iteratief, visueel en met focus op beleving. Van vraag naar oplossing.
        </p>
      </motion.div>
      {/* Process Flow Visualization */}
      <div className="mb-6 md:mb-8 flex items-center justify-center gap-2 md:gap-3 flex-wrap" data-id="element-335">
        {processSteps.map((step, index) => <Fragment key={step.id} data-id="element-336">
            <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: index * 0.1
        }} className="flex flex-col items-center" data-id="element-337">
              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`} data-id="element-338">
                <step.icon className="w-5 h-5 md:w-6 md:h-6 text-white" data-id="element-339" />
              </div>
              <span className="text-[10px] md:text-xs mt-1 md:mt-2 font-medium text-center" data-id="element-340">
                {step.title}
              </span>
            </motion.div>
            {index < processSteps.length - 1 && <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 opacity-40 hidden sm:block" data-id="element-341" />}
          </Fragment>)}
      </div>
      {/* Mobile: Vertical Stack Layout */}
      {isMobile && <div className="space-y-4" data-id="element-342">
          {processSteps.map((step, index) => {
        const StepIcon = step.icon;
        return <motion.div key={step.id} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1 + index * 0.1
        }} onClick={() => setSelectedStep(selectedStep === index ? null : index)} className="relative" data-id="element-343">
                <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
            boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
            transform: `rotate(${step.rotation}deg)`
          }} data-id="element-344">
                  {/* Header with gradient */}
                  <div className={`relative p-5 bg-gradient-to-br ${step.color} text-white`} data-id="element-345">
                    <div className="flex items-center gap-3 mb-2" data-id="element-346">
                      <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-347">
                        <StepIcon className="w-6 h-6 text-white" data-id="element-348" />
                      </div>
                      <div data-id="element-349">
                        <h3 className="text-xl font-bold" data-id="element-350">{step.title}</h3>
                        <p className="text-white/80 text-sm" data-id="element-351">{step.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed" data-id="element-352">
                      {step.description}
                    </p>
                  </div>
                  {/* Content - only show when selected */}
                  <AnimatePresence data-id="element-353">
                    {selectedStep === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-354">
                        <div className="p-5 space-y-5" data-id="element-355">
                          {/* Activities */}
                          <div data-id="element-356">
                            <h4 className="font-bold text-base mb-3 flex items-center gap-2" data-id="element-357">
                              <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center`} data-id="element-358">
                                <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-359" />
                              </div>
                              Activiteiten
                            </h4>
                            <ul className="space-y-2" data-id="element-360">
                              {step.activities.map((activity, i) => <motion.li key={i} initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: i * 0.1
                      }} className="flex items-start gap-3" data-id="element-361">
                                  <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-362">
                                    <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-363" />
                                  </div>
                                  <span className="text-sm opacity-80" data-id="element-364">
                                    {activity}
                                  </span>
                                </motion.li>)}
                            </ul>
                          </div>
                          {/* Benefits */}
                          <div className={`p-4 rounded-xl ${themeClasses.card} border ${themeClasses.border}`} data-id="element-365">
                            <h4 className="font-bold text-base mb-3" data-id="element-366">
                              Voordelen voor jou
                            </h4>
                            <ul className="space-y-2" data-id="element-367">
                              {step.benefits.map((benefit, i) => <motion.li key={i} initial={{
                        opacity: 0,
                        x: 10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: i * 0.1
                      }} className="flex items-start gap-2" data-id="element-368">
                                  <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-500" data-id="element-369" />
                                  <span className="text-sm opacity-80" data-id="element-370">
                                    {benefit}
                                  </span>
                                </motion.li>)}
                            </ul>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                  {/* Click indicator */}
                  {selectedStep !== index && <div className="p-3 text-center border-t border-white/10" data-id="element-371">
                      <span className="text-xs opacity-60" data-id="element-372">
                        Tik voor details
                      </span>
                    </div>}
                </div>
                {/* Pin effect */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-373" />
              </motion.div>;
      })}
        </div>}
      {/* Desktop: Grid Layout instead of Pinboard */}
      {!isMobile && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto" data-id="element-374">
          {processSteps.map((step, index) => {
        const StepIcon = step.icon;
        return <motion.div key={step.id} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2 + index * 0.1
        }} onHoverStart={() => setHoveredStep(index)} onHoverEnd={() => setHoveredStep(null)} onClick={() => setSelectedStep(selectedStep === index ? null : index)} className="cursor-pointer" data-id="element-375">
                <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl transition-all ${hoveredStep === index || selectedStep === index ? 'shadow-2xl scale-[1.02]' : 'shadow-lg'}`} data-id="element-376">
                  {/* Header with gradient */}
                  <div className={`relative p-6 bg-gradient-to-br ${step.color} text-white`} data-id="element-377">
                    <div className="flex items-center gap-4 mb-3" data-id="element-378">
                      <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-379">
                        <StepIcon className="w-7 h-7 text-white" data-id="element-380" />
                      </div>
                      <div data-id="element-381">
                        <h3 className="text-2xl font-bold" data-id="element-382">{step.title}</h3>
                        <p className="text-white/80 text-sm" data-id="element-383">{step.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed" data-id="element-384">
                      {step.description}
                    </p>
                  </div>
                  {/* Content - only show when selected */}
                  <AnimatePresence data-id="element-385">
                    {selectedStep === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-386">
                        <div className="p-6 space-y-6" data-id="element-387">
                          {/* Activities */}
                          <div data-id="element-388">
                            <h4 className="font-bold text-lg mb-3 flex items-center gap-2" data-id="element-389">
                              <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center`} data-id="element-390">
                                <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-391" />
                              </div>
                              Activiteiten
                            </h4>
                            <ul className="space-y-2" data-id="element-392">
                              {step.activities.map((activity, i) => <motion.li key={i} initial={{
                        opacity: 0,
                        x: -10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: i * 0.1
                      }} className="flex items-start gap-3" data-id="element-393">
                                  <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-394">
                                    <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-395" />
                                  </div>
                                  <span className="text-sm opacity-80" data-id="element-396">
                                    {activity}
                                  </span>
                                </motion.li>)}
                            </ul>
                          </div>
                          {/* Benefits */}
                          <div className={`p-4 rounded-xl ${themeClasses.card} border ${themeClasses.border}`} data-id="element-397">
                            <h4 className="font-bold text-base mb-3" data-id="element-398">
                              Voordelen voor jou
                            </h4>
                            <ul className="space-y-2" data-id="element-399">
                              {step.benefits.map((benefit, i) => <motion.li key={i} initial={{
                        opacity: 0,
                        x: 10
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: i * 0.1
                      }} className="flex items-start gap-2" data-id="element-400">
                                  <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-500" data-id="element-401" />
                                  <span className="text-sm opacity-80" data-id="element-402">
                                    {benefit}
                                  </span>
                                </motion.li>)}
                            </ul>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                  {/* Click indicator */}
                  {selectedStep !== index && <div className="p-4 text-center border-t border-white/10" data-id="element-403">
                      <span className="text-xs opacity-60" data-id="element-404">
                        Klik voor details
                      </span>
                    </div>}
                </div>
              </motion.div>;
      })}
        </div>}
    </div>;
}